import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, AlertTriangle, CheckCircle2, XCircle, FileSpreadsheet, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

// Bulk upload of operating-card files (AI reads number + expiry) + card picker + over-limit warning.
export const OpCardsToolbar = ({ data, selected, onSelect, onUploaded }) => {
  const ref = useRef(null);
  const xref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [job, setJob] = useState(null);

  const track = (job_id, total, label, kind) => {
    setJob({ status: "running", done: 0, total, kind });
    const poll = async () => {
      const j = await api.operatingCardsImportJob(job_id).catch(() => null);
      if (!j) { setTimeout(poll, 5000); return; }
      setJob({ ...j, kind });
      setResults(j.results.map((r) => ({ ...r, file: r.file || r.number || r.plate || "—" })));
      if (j.status === "done") {
        if (j.stalled) toast.error("توقفت القراءة قبل اكتمالها — أعد رفع الملفات التي لم تظهر نتيجتها");
        else toast.success(`${label}: ${j.results.filter((r) => r.ok).length} من ${j.total}`);
        setBusy(false); onUploaded?.();
      } else setTimeout(poll, 4000);
    };
    setTimeout(poll, 3000);
  };

  const onExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const { job_id, total } = await api.importOperatingCardsExcel(f);
      toast.info(`بدأ استيراد ${total} كرت — يقرأ النظام الملفات من الروابط`);
      track(job_id, total, "اكتمل الاستيراد", "excel");
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر قراءة ملف Excel"); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const { job_id, total } = await api.bulkUploadOperatingCards(files);
      toast.info(`تم رفع ${total} ملف — جارٍ قراءة الكروت`);
      track(job_id, total, "تمت قراءة الكروت", "files");
    } catch (err) {
      setBusy(false);
      const st = err?.response?.status;
      toast.error(err?.response?.data?.detail || (st === 413 ? "حجم الملفات كبير — ارفعها على دفعات أصغر" : st ? `تعذّر رفع الكروت (رمز ${st})` : "تعذّر رفع الكروت — تحقق من الاتصال وأعد المحاولة"));
    }
  };

  return (
    <div className="space-y-3">
      {data.over_limit_count > 0 && (
        <button type="button" data-testid="opcards-over-limit-banner" onClick={() => onSelect("over")}
          className="w-full flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 text-right hover:bg-rose-100 transition-colors">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {data.over_limit_count} كرت تجاوز {data.max_riders} مناديب — اضغط للعرض
        </button>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" multiple accept="image/*,application/pdf" hidden onChange={onFiles} data-testid="opcards-bulk-input" />
        <Button type="button" data-testid="opcards-bulk-upload-btn" disabled={busy} onClick={() => ref.current?.click()} className="gap-1.5 bg-amber-600 hover:bg-amber-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {busy ? (job?.kind === "files" ? `جارٍ قراءة الكروت ${job.done}/${job.total}` : "جارٍ الرفع…") : "إرفاق الكروت والاستمارات"}
        </Button>
        <input ref={xref} type="file" accept=".xlsx,.xls" hidden onChange={onExcel} data-testid="opcards-excel-input" />
        <Button type="button" variant="outline" data-testid="opcards-excel-btn" disabled={job?.status === "running" && job?.kind === "excel"} onClick={() => xref.current?.click()} className="gap-1.5">
          {job?.status === "running" && job?.kind === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />}
          {job?.status === "running" && job?.kind === "excel" ? `جارٍ الاستيراد ${job.done}/${job.total}` : "استيراد من Excel"}
        </Button>
        <Button type="button" variant="outline" data-testid="opcards-export-btn" disabled={!data.cards.length} className="gap-1.5"
          onClick={async () => { try { await saveBlob(await api.exportOperatingCards(), "كروت_التشغيل.xlsx"); } catch { toast.error("تعذّر التصدير"); } }}>
          <Download className="h-4 w-4 text-sky-600" /> تصدير Excel
        </Button>
        <Select value={selected} onValueChange={onSelect}>
          <SelectTrigger data-testid="opcards-select" className="w-56 h-10"><SelectValue placeholder="اختر كرتاً" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الكروت ({data.cards.length})</SelectItem>
            {[["3", "3 مناديب فأكثر"], ["2", "2 مندوب"], ["1", "1 مندوب"], ["0", "بدون مندوب"]].map(([t, l]) => (
              <SelectItem key={t} value={`tier:${t}`}>كروت {l}</SelectItem>
            ))}
            <SelectItem value="dup">المناديب المكررون</SelectItem>
            {data.over_limit_count > 0 && <SelectItem value="over">تجاوزت الحدّ ({data.over_limit_count})</SelectItem>}
            {data.cards.map((c) => (
              <SelectItem key={c.number} value={c.number}>{c.number}{c.plate_number ? ` (${c.plate_number})` : ""} — {c.riders_count} مندوب{c.operating_card_file_id ? " · مرفق" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {results && (
        <div data-testid="opcards-bulk-results" className="rounded-xl border border-slate-200 bg-slate-50 p-2 max-h-40 overflow-y-auto space-y-1">
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-rose-600" />}
              <span className="truncate max-w-[45%] text-slate-600">{r.file}</span>
              {r.ok ? <span className="text-slate-700">{r.kind === "استمارة" ? "استمارة للكرت " : "كرت "}<b dir="ltr">{r.number}</b>{r.plate ? ` · لوحة ${r.plate}` : ""}{r.expiry ? ` · ينتهي ${r.expiry}` : ""} · {r.employees_updated} مندوب{r.registration ? ` · الاستمارة: ${r.registration}` : ""}</span>
                : <span className="text-rose-600">{r.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
